/**
 * Test Schedules API Client
 * For therapists to prescribe home tests to patients
 */
import { apiClient } from '../api-client';

export type TestType = 'SPIROMETRY' | 'SIX_MWT';
export type TestFrequency = 'ONCE' | 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';

export interface TestScheduleCreate {
  patient_id: number;
  test_type: TestType;
  frequency: TestFrequency;
  start_date: string; // YYYY-MM-DD
  end_date?: string;  // YYYY-MM-DD
  instructions?: string;
}

export interface TestScheduleUpdate {
  test_type?: TestType;
  frequency?: TestFrequency;
  start_date?: string;
  end_date?: string;
  instructions?: string;
  is_active?: boolean;
}

export interface TherapistInfo {
  id: number;
  name: string;
  license_number: string;
  specialization: string;
}

export interface PatientInfo {
  id: number;
  umr_no: string;
  name: string;
}

export interface TestSchedule {
  id: number;
  patient_id: number;
  prescribed_by_therapist_id: number;
  test_type: string;
  frequency: string;
  start_date: string;
  end_date?: string;
  instructions?: string;
  is_active: boolean;
  created_at: string;
  patient?: PatientInfo;
  prescribed_by?: TherapistInfo;
}

export interface TestSchedulesResponse {
  schedules: TestSchedule[];
  total: number;
  patient_id?: number;
}

export const testSchedulesApi = {
  /**
   * Create a new test schedule (Therapist only)
   */
  createSchedule: async (data: TestScheduleCreate): Promise<{
    status: string;
    message: string;
    schedule_id: number;
    patient_id: number;
    test_type: string;
    frequency: string;
  }> => {
    return apiClient.post('/test-schedules/schedules', data);
  },

  /**
   * Get all test schedules created by current therapist
   */
  getMySchedules: async (params?: {
    is_active?: boolean;
    test_type?: TestType;
    patient_id?: number;
  }): Promise<TestSchedulesResponse> => {
    return apiClient.get('/test-schedules/schedules/therapist/me', params);
  },

  /**
   * Get test schedules for a specific patient (Therapist view)
   */
  getPatientSchedules: async (
    patientId: number,
    params?: {
      is_active?: boolean;
      test_type?: TestType;
    }
  ): Promise<TestSchedulesResponse> => {
    return apiClient.get(`/test-schedules/schedules/patient/${patientId}`, params);
  },

  /**
   * Get my scheduled tests (Patient view)
   */
  getMyScheduledTests: async (params?: {
    is_active?: boolean;
    test_type?: TestType;
  }): Promise<TestSchedulesResponse> => {
    return apiClient.get('/test-schedules/schedules/patient/me', params);
  },

  /**
   * Update a test schedule
   */
  updateSchedule: async (
    scheduleId: number,
    data: TestScheduleUpdate
  ): Promise<{
    status: string;
    message: string;
    schedule_id: number;
  }> => {
    return apiClient.put(`/test-schedules/schedules/${scheduleId}`, data);
  },

  /**
   * Delete a test schedule
   */
  deleteSchedule: async (scheduleId: number): Promise<{
    status: string;
    message: string;
    schedule_id: number;
  }> => {
    return apiClient.delete(`/test-schedules/schedules/${scheduleId}`);
  },
};
